import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Shield,
  AlertTriangle,
  Eye,
  Heart,
  Users,
  MessageCircleWarning,
  ThumbsDown,
  XCircle,
  Sparkles,
  BookOpen,
  Activity,
  RefreshCw,
  CreditCard,
  FileText,
  ShieldCheck,
} from "lucide-react";
import SEO from "@/components/SEO";
import { usePageSeo } from "@/hooks/usePageSeo";
import CalendlyInlineBooking from "@/components/CalendlyInlineBooking";
import CareCreditBanner from "@/components/CareCreditBanner";
import { RelationshipGuideCompanion } from "@/components/RelationshipGuideCompanion";

const PAGE_KEY = "boundaries-therapist-specialty";

const AREA_LINKS = [
  { id: "miami", to: "/specialties/online-ketamine-therapy-women-miami", label: "Miami, Florida" },
  { id: "cumberland", to: "/specialties/ketamine-therapy-hyper-independent-women-cumberland-maine", label: "Cumberland, Maine" },
  { id: "portland", to: "/specialties/ketamine-therapy-hyper-independent-women-portland-maine", label: "Portland, Maine" },
  { id: "dc", to: "/specialties/ketamine-therapy-hyper-independent-women-washington-dc", label: "Washington, D.C." },
];

const silentBurdens = [
  {
    icon: AlertTriangle,
    title: "Chronic Guilt",
    body: "Believing that saying \"no,\" setting a boundary, or prioritizing yourself makes you a \"bad child.\"",
  },
  {
    icon: Shield,
    title: "Porous or Rigid Boundaries",
    body: "Alternating between over-explaining your personal life to keep the peace and completely shutting down to avoid conflict.",
  },
  {
    icon: Eye,
    title: "Emotional Hyper-Vigilance",
    body: "Constantly reading between the lines of text messages or phone calls for subtle shifts in tone.",
  },
  {
    icon: Users,
    title: "Codependency & Enmeshment",
    body: "Sacrificing your own career, relationship, or mental health to fix family drama or soothe a parent's emotions.",
  },
];

const parentTraits = [
  {
    icon: Heart,
    title: "Parentification",
    body: "Expecting you to act as their emotional anchor, confidant, or problem-solver.",
  },
  {
    icon: MessageCircleWarning,
    title: "Guilt-Tripping & Manipulation",
    body: "Weaponizing obligation or shame whenever you express independent choices.",
  },
  {
    icon: ThumbsDown,
    title: "Volatile or Passive-Aggressive Reactions",
    body: "Reacting with anger, silent treatments, or victimhood when faced with simple boundaries.",
  },
  {
    icon: XCircle,
    title: "Dismissal of Your Reality",
    body: "Denying past emotional neglect or invalidating your feelings with \"after everything I did for you.\"",
  },
];

const therapySteps = [
  {
    icon: Sparkles,
    title: "Guilt-Free Boundary Setting",
    body: "Practice exact scripts and strategies to say \"no,\" limit communication, or handle holidays without over-explaining or apologizing.",
  },
  {
    icon: BookOpen,
    title: "Reparenting & Inner Child Healing",
    body: "Process unmet emotional needs from childhood so you stop seeking validation from people who cannot give it.",
  },
  {
    icon: Activity,
    title: "Nervous System Regulation",
    body: "Use somatic techniques and trauma processing to release the physical anxiety and chest-tightening dread that accompanies family interactions.",
  },
  {
    icon: RefreshCw,
    title: "Breaking Codependent Cycles",
    body: "Learn to tolerate other people's emotional discomfort without compulsively rushing in to fix it.",
  },
];

const faqs = [
  {
    q: "How do I know if I need a therapist for emotionally immature parents?",
    a: "If family interactions leave you feeling drained, anxious, guilty, or feeling like you are walking on eggshells, specialized therapy can help. Common signs include struggle with boundary setting, chronic people-pleasing, and feelings of emotional exhaustion after contacting your family.",
  },
  {
    q: "Can I set boundaries with my parents without cutting them off?",
    a: "Yes. Boundaries are not about cutting people out; they are clear guidelines for how you protect your peace while maintaining a level of contact that feels safe for you. Therapy helps you establish low-contact, conversational, or emotional boundaries tailored to your comfort level.",
  },
  {
    q: "Is therapy available online in Florida, Maine, and Washington, D.C.?",
    a: "Yes. We provide specialized online therapy for adult children of emotionally immature parents across Florida (including Miami, Pembroke Pines, and Fort Lauderdale), Maine, and Washington, D.C. Virtual sessions allow you to do this deep work from the comfort and privacy of your home.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const SpecialtyBoundariesTherapistPage = () => {
  const seoOverride = usePageSeo(PAGE_KEY);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="pt-20" data-testid="specialty-boundaries-therapist-page">
      <SEO
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        title={
          seoOverride?.meta_title ||
          "Boundaries Therapist for Adult Children of Emotionally Immature Parents"
        }
        description={
          seoOverride?.meta_description ||
          "Virtual, specialized therapy for adult children of emotionally immature parents. Learn guilt-free boundary setting in Miami FL, Maine, and Washington DC."
        }
        canonical="https://divinelovewellness.net/specialties/boundaries-therapist-emotionally-immature-parents"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* === Hero === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-5">
              Specialty · Boundaries Therapist
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-tight">
              Boundaries Therapist for Adult Children of Emotionally Immature Parents: heal childhood dynamics, and reclaim your peace with virtual specialized therapy.
            </h1>
            <h2 className="mt-4 font-serif text-xl sm:text-2xl lg:text-3xl italic font-medium text-primary leading-snug">
              Learn how to set guilt-free boundaries. Stop carrying the weight of family caretaking.
            </h2>

            <figure className="mt-8" data-testid="boundaries-hero-image">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/ktntgwbc_IMG_5919.jpeg"
                alt="An adult daughter standing with crossed arms looking away while an older woman reaches toward her outside a suburban home"
                loading="lazy"
                className="w-full rounded-2xl object-cover shadow-soft"
              />
              <figcaption className="mt-3 text-sm text-muted-foreground italic text-center">
                Exploring the impact of toxic parents &mdash; and finding the strength to draw boundaries.
              </figcaption>
            </figure>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="lg"
                  className="rounded-full px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="boundaries-hero-cta"
                >
                  Book Free 40-min Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 py-6 border-primary/30 text-primary hover:bg-primary/5"
                >
                  Ask Laura a Question
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === What Is === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Definition
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              What is a{" "}
              <span className="italic text-primary">Boundaries Therapist</span>{" "}
              for Adult Children of Emotionally Immature Parents?
            </h2>
            <p className="mt-8 text-base md:text-lg text-foreground/80 leading-[1.85]">
              A boundaries therapist for adult children of emotionally immature parents is a licensed mental health professional who specializes in helping adults identify toxic family dynamics, set clear limits without overwhelming guilt, and recover from emotional neglect or parentification. Through trauma-informed care &mdash; including <strong className="text-foreground">Somatic Therapy, EMDR, and Internal Family Systems (IFS)</strong> &mdash; clients learn to break codependent cycles, regulate their nervous system, and establish healthy autonomy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* === The Silent Burden === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              The Weight You Carry
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              The Silent Burden of Being the{" "}
              <span className="italic text-primary">&ldquo;Strong One&rdquo;</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Do you constantly feel responsible for managing your parents&rsquo; reactions, moods, or expectations? Growing up with emotionally immature or narcissistic parents often forces children into an adult role far too early. As an adult, these early survival strategies manifest in daily struggles:
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {silentBurdens.map((item) => (
              <Card
                key={item.title}
                className="p-6 md:p-7 bg-white border-border/40 rounded-2xl"
                data-testid={`boundaries-burden-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
              >
                <div className="p-3 rounded-full bg-primary/10 inline-block">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-foreground mt-4">
                  {item.title}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground mt-2 leading-relaxed">
                  {item.body}
                </p>
              </Card>
            ))}
          </motion.div>
        </div>
      </section>

      {/* === Recognizing Traits === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Know The Patterns
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Recognizing{" "}
              <span className="italic text-primary">Emotionally Immature Parenting Traits</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Emotionally immature parents typically lack emotional self-awareness, empathy, and the capacity for authentic intimacy. Common traits include:
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="space-y-4">
            {parentTraits.map((trait) => (
              <div
                key={trait.title}
                className="flex items-start gap-4 md:gap-5 p-5 md:p-6 bg-secondary/30 border border-border/40 rounded-2xl"
                data-testid={`boundaries-trait-${trait.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
              >
                <div className="p-3 rounded-full bg-accent/15 flex-shrink-0">
                  <trait.icon className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-semibold text-foreground">
                    {trait.title}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mt-1 leading-relaxed">
                    {trait.body}
                  </p>
                </div>
              </div>
            ))}
          </motion.div>

          <motion.div
            {...fadeUp}
            className="mt-14 max-w-3xl mx-auto rounded-3xl border border-primary/15 bg-white/90 backdrop-blur shadow-soft px-6 md:px-10 py-8 md:py-10"
            data-testid="boundaries-areas-box"
          >
            <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-semibold text-foreground leading-snug text-center">
              Offering Virtual Ketamine-Assisted Therapy in these spots:
            </h2>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-base md:text-lg">
              {AREA_LINKS.map((area) => (
                <li key={area.id}>
                  <Link
                    to={area.to}
                    className="inline-flex items-center gap-2 text-primary font-medium underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors"
                    data-testid={`boundaries-area-link-${area.id}`}
                  >
                    {area.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* === How Therapy Restores Your Autonomy === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4 text-center">
              Practical Healing
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight text-center">
              How Therapy{" "}
              <span className="italic text-primary">Restores Your Autonomy</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-[1.85] text-center max-w-2xl mx-auto">
              Therapy isn&rsquo;t about changing your parents &mdash; it is about changing your relationship with yourself. By working with a specialized therapist, you will gain practical, actionable tools to step out of the caretaker role:
            </p>

            <ol className="mt-12 space-y-5">
              {therapySteps.map((step, idx) => (
                <li
                  key={step.title}
                  className="flex items-start gap-5 p-6 bg-white border border-border/40 rounded-2xl"
                  data-testid={`boundaries-step-${idx + 1}`}
                >
                  <div className="flex items-center justify-center h-11 w-11 rounded-full bg-primary text-primary-foreground font-serif text-lg font-semibold flex-shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-lg font-semibold text-foreground flex items-center gap-2">
                      <step.icon className="h-4 w-4 text-primary" />
                      {step.title}
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground mt-1 leading-relaxed">
                      {step.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </motion.div>
        </div>
      </section>

      {/* === Payment & HSA/FSA === */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-5 rounded-2xl border border-primary/30 bg-primary/5 px-6 py-5 mb-5">
              <div className="flex items-start gap-4 flex-1">
                <CreditCard className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm uppercase tracking-[0.18em] text-primary font-semibold">
                    Extended Payment Plans Available
                  </p>
                  <p className="mt-1 font-serif text-lg md:text-xl text-foreground">
                    For as low as <span className="font-semibold text-primary">$125 bi-weekly</span>.
                  </p>
                </div>
              </div>
              <a
                href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto flex-shrink-0"
              >
                <Button
                  className="w-full sm:w-auto rounded-full px-6 py-5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="boundaries-payment-plan-cta"
                >
                  Book Free 40-min Call
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border/40 bg-secondary/30">
                <CreditCard className="h-4 w-4 text-accent flex-shrink-0" />
                <span className="text-sm text-foreground/80">HSA &amp; FSA Cards Accepted</span>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border/40 bg-secondary/30">
                <FileText className="h-4 w-4 text-accent flex-shrink-0" />
                <span className="text-sm text-foreground/80">Superbills for OON Reimbursement</span>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border/40 bg-secondary/30">
                <ShieldCheck className="h-4 w-4 text-accent flex-shrink-0" />
                <span className="text-sm text-foreground/80">HIPAA-Aligned &amp; Confidential</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === Community Guide — relationship anxiety gift === */}
      <RelationshipGuideCompanion background="bg-accent/10" />

      {/* === FAQ === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Frequently Asked Questions
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Therapy for{" "}
              <span className="italic text-primary">Adult Children</span>
            </h2>
          </motion.div>

          <motion.div {...fadeUp} className="space-y-5">
            {faqs.map((faq, idx) => (
              <Card
                key={idx}
                className="p-6 md:p-7 bg-white border-border/40 rounded-2xl"
                data-testid={`boundaries-faq-${idx + 1}`}
              >
                <h3 className="font-serif text-lg md:text-xl font-semibold text-foreground">
                  {idx + 1}. {faq.q}
                </h3>
                <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                  {faq.a}
                </p>
              </Card>
            ))}
          </motion.div>
        </div>
      </section>

      {/* === Closing CTA === */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <Sparkles className="h-8 w-8 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Ready to Protect Your Peace{" "}
              <span className="italic text-primary">Without the Guilt?</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              You don&rsquo;t have to spend the rest of your life managing your family&rsquo;s emotional state at the expense of your own. Healing begins the moment you decide your boundaries matter.
            </p>
            <div className="mt-8">
              <a
                href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="lg"
                  className="rounded-full px-10 py-6 bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="boundaries-closing-cta"
                >
                  Schedule a Free 40 Minute Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === CareCredit Financing Banner === */}
      <CareCreditBanner />

      {/* === Inline Calendly Booking === */}
      <CalendlyInlineBooking background="bg-secondary/30" />
    </div>
  );
};

export default SpecialtyBoundariesTherapistPage;
